<script setup lang="ts" generic="T extends { lat: number; lng: number }">
/* global google */
import type { Cluster, MarkerClustererOptions, Renderer } from "@googlemaps/markerclusterer";

import { GOOGLE_MAPS_API_VERSION } from "@voltalis/frontend-utils";
import { computed, toRaw, useTemplateRef, watch } from "vue";
import { GoogleMap, MarkerCluster } from "vue3-google-map";

import { handleClusterClick } from "@/components/ui/map/map-spiderfy";
import { mapStyle } from "@/components/ui/map/map-style";
import { EZoomDirection, type IMapExpose, type IMapPoint } from "@/components/ui/map/map.types";
import { useMapSpiderfy } from "@/components/ui/map/use-map-spiderfy";
import { useServices } from "@/configuration/plugins/app/app-provider.configuration";

import MapDefaultMarker from "@/components/ui/map/map-default-marker.vue";

const FRANCE_CENTER_LAT = 46.76667;
const FRANCE_CENTER_LNG = 2.45;
const FRANCE_ZOOM = 6;
const CLUSTER_ICON_SIZE = 75;
const FALLBACK_MARKER_WIDTH = 180;
const MAX_ZOOM = 16;
const MIN_ZOOM = 3;

const clusterSvg = window.btoa(`
  <svg fill="#006AFF" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
    <circle cx="120" cy="120" opacity=".3" r="70" />
    <circle cx="120" cy="120" opacity="1" r="50" />
  </svg>`);

interface IProps {
  markers?: Array<T>;
  centerCameraPadding?: number | google.maps.Padding;
}

const props = defineProps<IProps>();

const { env } = useServices();

const map = useTemplateRef<InstanceType<typeof GoogleMap>>("map");
const apiKey = env.VITE_GOOGLE_MAPS_API_KEY;
const markerKeys = new WeakMap<object, number>();
let nextMarkerKey = 0;
const spiderfy = useMapSpiderfy({ fallbackMarkerWidth: FALLBACK_MARKER_WIDTH });

const clusterRenderer: Renderer = {
  render(cluster) {
    return new google.maps.Marker({
      label: {
        text: String(cluster.count),
        color: "white",
      },
      position: cluster.position,
      icon: {
        url: `data:image/svg+xml;base64,${clusterSvg}`,
        scaledSize: new google.maps.Size(CLUSTER_ICON_SIZE, CLUSTER_ICON_SIZE),
        anchor: new google.maps.Point(CLUSTER_ICON_SIZE / 2, CLUSTER_ICON_SIZE / 2),
      },
      visible: !spiderfy.isClusterActive(cluster),
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + cluster.count,
    });
  },
};

const isMapReady = computed(() => {
  return map.value?.ready ?? false;
});

const googleMap = computed(() => {
  return map.value?.map;
});

const markers = computed(() => {
  return props.markers ?? [];
});
const markerSnapshot = computed(() => [...markers.value]);
const cameraPadding = computed<Required<google.maps.Padding>>(() => {
  const padding = props.centerCameraPadding;
  if (typeof padding === "number") {
    return {
      bottom: padding,
      left: padding,
      right: padding,
      top: padding,
    };
  }

  return {
    bottom: padding?.bottom ?? 0,
    left: padding?.left ?? 0,
    right: padding?.right ?? 0,
    top: padding?.top ?? 0,
  };
});

function getMarkerKey(marker: T) {
  const rawMarker = toRaw(marker);
  const existingKey = markerKeys.get(rawMarker);
  if (existingKey !== undefined) {
    return existingKey;
  }

  const markerKey = nextMarkerKey++;
  markerKeys.set(rawMarker, markerKey);
  return markerKey;
}

const keyedMarkers = computed(() => {
  return markers.value.map((marker) => ({ key: getMarkerKey(marker), marker }));
});

function getSpiderfyOffset(marker: T) {
  return spiderfy.getOffset(marker);
}

function closeSpiderfy() {
  spiderfy.close();
}

function handleClusterClicked(_event: google.maps.MapMouseEvent, cluster: Cluster, gmap: google.maps.Map) {
  handleClusterClick({
    currentZoom: gmap.getZoom(),
    maximumZoom: MAX_ZOOM,
    openNativeCluster: () => {
      if (cluster.bounds) {
        gmap.fitBounds(cluster.bounds, cameraPadding.value);
      }
    },
    openSpiderfy: () => {
      const position = cluster.position;
      panCameraToPoint(gmap, { lat: position.lat(), lng: position.lng() });
      spiderfy.open(cluster, gmap);
    },
  });
}

function handleClusteringEnded(clusterer: unknown) {
  const gmap = googleMap.value;
  if (
    !gmap ||
    typeof clusterer !== "object" ||
    clusterer === null ||
    !("clusters" in clusterer) ||
    !Array.isArray(clusterer.clusters)
  ) {
    return;
  }

  spiderfy.reconcileClusters(clusterer.clusters as Array<Cluster>, gmap);
}

const clusterOptions: MarkerClustererOptions = {
  algorithmOptions: { maxZoom: MAX_ZOOM + 1 },
  renderer: clusterRenderer,
  onClusterClick: handleClusterClicked,
};

function getPaddedCameraCenter(gmap: google.maps.Map, point: IMapPoint, zoom: number) {
  const projection = gmap.getProjection();
  const projectedPoint = projection?.fromLatLngToPoint(new google.maps.LatLng(point));
  if (!projection || !projectedPoint) {
    return point;
  }

  const scale = 2 ** zoom;
  const padding = cameraPadding.value;
  const projectedCenter = new google.maps.Point(
    projectedPoint.x + (padding.right - padding.left) / (2 * scale),
    projectedPoint.y + (padding.bottom - padding.top) / (2 * scale),
  );

  return projection.fromPointToLatLng(projectedCenter) ?? point;
}

function panCameraToPoint(gmap: google.maps.Map, point: IMapPoint, zoom = gmap.getZoom() ?? FRANCE_ZOOM) {
  gmap.panTo(getPaddedCameraCenter(gmap, point, zoom));
}

function centerCameraToFrance() {
  const gmap = googleMap.value;
  if (!isMapReady.value || !gmap) {
    // Do not touch camera if neither map is ready, nor google map api is defined
    return;
  }

  const franceCenter = {
    lat: FRANCE_CENTER_LAT,
    lng: FRANCE_CENTER_LNG,
  };
  gmap.setZoom(FRANCE_ZOOM);
  gmap.setCenter(getPaddedCameraCenter(gmap, franceCenter, FRANCE_ZOOM));
}

function centerCameraToClusterBounds() {
  const gmap = googleMap.value;
  if (!isMapReady.value || !gmap) {
    // Do not touch camera if neither map is ready, nor google map api is defined
    return;
  }

  if (markers.value.length === 0) {
    centerCameraToFrance();
    return;
  }

  const bounds = new google.maps.LatLngBounds();
  markers.value.forEach((marker) => bounds.extend(marker));
  gmap.fitBounds(bounds, cameraPadding.value);
}

function centerCameraToPoint(point: IMapPoint) {
  const gmap = googleMap.value;
  if (!isMapReady.value || !gmap) {
    // Do not touch camera if neither map is ready, nor google map api is defined
    return;
  }

  panCameraToPoint(gmap, point);
}

function zoom(direction: EZoomDirection) {
  const gmap = googleMap.value;
  if (!isMapReady.value || !gmap) return;

  const zoom: number = gmap.getZoom() ?? FRANCE_ZOOM;
  gmap.setZoom(direction === EZoomDirection.IN ? zoom + 1 : zoom - 1);
}

watch(
  [isMapReady, googleMap, markerSnapshot],
  () => {
    closeSpiderfy();
    if (!isMapReady.value) {
      return;
    }

    centerCameraToClusterBounds();
  },
  { immediate: true },
);

defineExpose<IMapExpose>({
  centerCameraToClusterBounds,
  centerCameraToFrance,
  centerCameraToPoint,
  zoom,
});
</script>

<template>
  <div class="relative flex flex-grow w-full">
    <GoogleMap
      ref="map"
      :api-key="apiKey"
      :version="GOOGLE_MAPS_API_VERSION"
      :disable-default-ui="true"
      :street-view-control="false"
      style="width: 100%; height: 100%"
      :styles="mapStyle"
      :max-zoom="MAX_ZOOM"
      :min-zoom="MIN_ZOOM"
      class="rounded-2xl overflow-clip"
      @click="closeSpiderfy"
      @dragstart="closeSpiderfy"
      @zoom_changed="closeSpiderfy"
    >
      <MarkerCluster
        :options="clusterOptions"
        @clusteringend="handleClusteringEnded"
      >
        <template
          v-for="keyedMarker in keyedMarkers"
          :key="keyedMarker.key"
        >
          <slot
            :marker="keyedMarker.marker"
            name="marker"
            :spiderfy-offset="getSpiderfyOffset(keyedMarker.marker)"
          >
            <MapDefaultMarker
              :marker="keyedMarker.marker"
              :spiderfy-offset="getSpiderfyOffset(keyedMarker.marker)"
            />
          </slot>
        </template>
      </MarkerCluster>
    </GoogleMap>
    <div class="absolute inset-0 pointer-events-none">
      <slot name="front" />
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
